import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Box, Alert, CircularProgress } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { dashboardService } from '../../services/dashboardService'
import {
  PersonalStats,
  SystemOverview,
  RecentTestResults,
  UpcomingDeadlines,
  RecentActivity,
  CategoryStats,
  ReminderStats,
} from '../../components/dashboard'
import { DashboardStats, AdminDashboardStats } from '../../types/dashboard'

export const SuperAdminDashboard: React.FC = () => {
  const { t } = useTranslation('dashboard')
  const { user } = useAuth()
  const navigate = useNavigate()
  const [userStats, setUserStats] = useState<DashboardStats | null>(null)
  const [adminStats, setAdminStats] = useState<AdminDashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let isMounted = true

    const loadDashboardData = async () => {
      if (!user) return

      try {
        setLoading(true)
        setError('')

        const [adminData, userData] = await Promise.all([
          dashboardService.getAdminDashboardStats(),
          dashboardService.getUserDashboardStats(),
        ])

        if (isMounted) {
          setAdminStats(adminData)
          setUserStats(userData)
        }
      } catch (err) {
        console.error('Dashboard data loading failed:', err)
        if (isMounted) setError(t('loadError'))
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadDashboardData()

    return () => {
      isMounted = false
    }
  }, [user, t])

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )
  }

  return (
    <Box>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}
      >
        {t('superAdminTitle')}
      </Typography>

      <Typography variant="body1" color="textSecondary" gutterBottom>
        {t('welcome', { name: user?.name })}
      </Typography>

      {userStats && (
        <PersonalStats stats={userStats} onEnrollmentsClick={() => navigate('/my-enrollments')} />
      )}

      {adminStats && (
        <SystemOverview
          stats={adminStats}
          onUsersClick={() => navigate('/users')}
          onComplianceClick={() => navigate('/compliance')}
          onTestsClick={() => navigate('/tests')}
        />
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: { xs: 2, sm: 3 },
        }}
      >
        {userStats && <RecentTestResults results={userStats.recentTestResults} />}
        {userStats && (
          <UpcomingDeadlines
            deadlines={userStats.upcomingDeadlines}
            onViewAll={() => navigate('/my-enrollments')}
          />
        )}
      </Box>

      {adminStats && (
        <Box sx={{ mt: 3 }}>
          <RecentActivity activities={adminStats.recentActivity} />
        </Box>
      )}

      {adminStats && <CategoryStats stats={adminStats.categoryStats} />}

      <ReminderStats />
    </Box>
  )
}
